import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'providers/CustomAuthProvider';
import { useZendeskChat } from 'hooks/useZendeskChat';
import './AppcuesMenu.css';

interface IAppcuesProp {
    page: () => void;
    loadLaunchpad: (id: string, options: any) => void;
    hideLaunchpad?: () => void;
    identify: (id: string, options: any) => void;
    group: (id: string, options: any) => void;
}

interface IAppcuesSettings {
    enableURLDetection: boolean;
}

declare global {
    interface Window {
        Appcues?: IAppcuesProp;
        AppcuesSettings?: IAppcuesSettings;
        safeZendeskShow?: () => void;
        zE?: any;
        zESettings?: any;
    }
}

const AppcuesMenu = () => {
    const [zendeskReady, setZendeskReady] = useState(false);
    const { user, clientId, isAdmin, isSystemAdmin, modules } = useAuth();
    const auth0Id = user?.sub;
    const appMetadata = user?.['https://enerex.com/app_metadata'] || {};
    const companyName = appMetadata?.CompanyName || appMetadata?.Company?.Name || clientId || 'DataHub';
    const givenName = user?.given_name || '';
    const familyName = user?.family_name || '';
    const userName = (givenName && familyName)
        ? `${givenName} ${familyName}`.trim()
        : (givenName || familyName || user?.name || user?.nickname || user?.email || '');
    const { openChat } = useZendeskChat({
        userName,
        userEmail: user?.email || '',
        companyName
    });

    const safeZendeskCall = useCallback((callback: () => void) => {
        if (typeof window.zE === 'function') {
            try {
                callback();
            } catch (error) {
                console.error('Error calling Zendesk:', error);
            }
        } else {
            console.warn('window.zE is not available yet');
        }
    }, []);

    const removeAppcuesBellIcon = useCallback(() => {
        document
            .querySelector<HTMLButtonElement>('#appcues-widget-icon')
            ?.classList.remove('appcues-icon-bell');
    }, []);

    useEffect(() => {
        const checkZendeskReady = () => {
            if (typeof window.zE === 'function') {
                setZendeskReady(true);
                return true;
            }
            return false;
        };

        if (checkZendeskReady()) {
            return;
        }

        let retryCount = 0;
        const maxRetries = 20;
        const interval = setInterval(() => {
            retryCount++;
            if (checkZendeskReady() || retryCount >= maxRetries) {
                clearInterval(interval);
                if (retryCount >= maxRetries) {
                    console.warn('Zendesk widget did not load after maximum retries');
                }
            }
        }, 500);

        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        if (zendeskReady) {
            safeZendeskCall(() => {
                window.zE('webWidget', 'hide');
            });
        }
    }, [zendeskReady, safeZendeskCall]);

    useEffect(() => {
        if (!window.Appcues || !user) return;

        window.Appcues.page();

        if (auth0Id) {
            fetchUserInfo(auth0Id);
        }

        window.Appcues.hideLaunchpad = function () {
            document
                .querySelector<HTMLButtonElement>('.appcues-widget-icon')
                ?.click();
        };

        const setupShowFunction = () => {
            if (typeof window.zE === 'function') {
                window.zE.show = function () {
                    window.Appcues?.hideLaunchpad?.();
                    openChat();
                    chatBotClose();
                };
            } else {
                setTimeout(setupShowFunction, 500);
            }
        };

        setupShowFunction();

        const handleZendeskShow = () => {
            if (typeof window.zE?.show === 'function') {
                window.zE.show();
            } else {
                console.warn('Zendesk is not available yet');
            }
        };

        window.safeZendeskShow = handleZendeskShow;

        window.Appcues.loadLaunchpad('#my-launchpad', {
            position: 'right',
            header: "<h5 class='mx-2'>Help Tutorials</h5>",
            footer: `
                <div class="app-cues-others" style="display: flex; align-items: flex-start; justify-content: center; gap: 28px; width: 100%; padding: 8px 12px 4px;">
                    <div class="app-cues-button-area" style="display: flex; flex-direction: column; align-items: center; width: 84px; text-align: center;">
                        <div>
                            <button type="button" class="btn app-cues-others-button btn-primary mb-2" style="display: inline-flex; align-items: center; justify-content: center; width: 42px; height: 42px; padding: 0;" onclick="window.safeZendeskShow()">
                                <i class="mdi mdi-message-text-outline" style="line-height: 1;"></i>
                            </button>
                        </div>
                        <label>Live Chat</label>
                    </div>
                    <div class="app-cues-button-area" style="display: flex; flex-direction: column; align-items: center; width: 84px; text-align: center;">
                        <div>
                            <a href="https://help.enerex.com/hc/en-us" target="_blank" rel="noreferrer" onclick="window.Appcues.hideLaunchpad();" class="btn app-cues-others-button btn-primary mb-2" style="display: inline-flex; align-items: center; justify-content: center; width: 42px; height: 42px; padding: 0;">
                                <i class="mdi mdi-information-outline" style="line-height: 1;"></i>
                            </a>
                        </div>
                        <label>Help Centre</label>
                    </div>
                    <div class="app-cues-button-area" style="display: flex; flex-direction: column; align-items: center; width: 84px; text-align: center;">
                        <div>
                            <a href="https://enerex.com/event/" target="_blank" rel="noreferrer" onclick="window.Appcues.hideLaunchpad();" class="btn app-cues-others-button btn-primary mb-2" style="display: inline-flex; align-items: center; justify-content: center; width: 42px; height: 42px; padding: 0;">
                                <i class="mdi mdi-calendar-month-outline" style="line-height: 1;"></i>
                            </a>
                        </div>
                        <label>Event Calendar</label>
                    </div>
                </div>`
        });

        removeAppcuesBellIcon();
        setTimeout(removeAppcuesBellIcon, 100);
        setTimeout(removeAppcuesBellIcon, 500);

        if (zendeskReady) {
            setUpZendeskWidget();
        }
    }, [user, auth0Id, zendeskReady, safeZendeskCall, removeAppcuesBellIcon, openChat]);

    const setUpZendeskWidget = () => {
        if (typeof window.zE !== 'undefined') {
            safeZendeskCall(() => {
                window.zE(function () {
                    window.zE.identify({
                        name: userName,
                        email: user?.email
                    });
                    chatBotClose();
                });
            });
        } else {
            setTimeout(setUpZendeskWidget, 500);
        }
    };

    const chatBotClose = () => {
        const frame = document.querySelector<HTMLIFrameElement>('#launcher');
        if (frame !== undefined) {
            const btnClose =
                frame?.contentDocument?.body.querySelector<HTMLButtonElement>(
                    'button[data-testid="launcher"]'
                );
            if (btnClose !== undefined && btnClose !== null) {
                btnClose.style.opacity = '0';
            }
        } else {
            setTimeout(chatBotClose, 500);
        }
    };

    const fetchUserInfo = (appcuesUserId: string) => {
        const appcuesIdentifyUserId = `DH - ${companyName} - ${user?.email || appcuesUserId}`;

        window.Appcues?.identify(appcuesIdentifyUserId, {
            role: appMetadata?.Roles?.join(', ') || null,
            email: user?.email,
            name: userName,
            companyName,
            product: 'Datahub-SP',
            isAdmin,
            isSystemAdmin,
            modules
        });

        window.Appcues?.group(companyName, {
            companyName,
            clientId: clientId || null
        });
    };

    return <div className="appcues-launchpad-root" id="my-launchpad" />;
};

export default AppcuesMenu;
